import { Routes } from '@angular/router';

import { BrandsComponent } from '@modules/brands/brands.component';

import { AuthGuard } from '@core/guards/auth.guard';
import { MobileGuard } from '@core/guards/mobile-guard/mobile.guard';
import { OutletResolver } from '@core/services/outlet/outlet-resolver';
import { RouteResolver } from '@app/core/services/page-navigation/route.resolver';

const routes: Routes = [
    {
        path: '',
        component: BrandsComponent,
        data: {
            pageTitle: 'BOX8',
            showAppDownloadOptions: true,
            hideHeader: true,
            showCart: true
        }
    },
    {
        path: 'profile',
        loadChildren: () => import('@modules/customer/customer.module').then(m => m.CustomerModule),
        data: {
            pageTitle: 'PROFILE',
            showCart: true
        },
        canActivate: [AuthGuard],
        resolve: { outletData: OutletResolver }
    },
    {
        path: 'profile/:sub_category',
        loadChildren: () => import('@modules/customer/customer.module').then(m => m.CustomerModule),
        data: {
            pageTitle: 'PROFILE'
        },
        canActivate: [AuthGuard]
    },
    {
        path: 'menu',
        loadChildren: () => import('@modules/menu/menu.module').then(m => m.MenuModule),
        data: {
            pageTitle: 'MENU',
            showCart: true,
            preloadPaths: ['']
        }
    },
    {
        path: 'menu/:category_path_name',
        loadChildren: () => import('@modules/menu/menu.module').then(m => m.MenuModule),
        data: {
            pageTitle: 'MENU',
            showCart: true
        }
    },
    {
        path: 'search',
        loadComponent: () =>
            import('@shared/components/product-search/product-search.component').then(m => m.ProductSearchComponent),
        data: {
            showCart: true
        }
    },
    {
        path: 'help',
        loadChildren: () => import('@modules/help-and-support/help-and-support.module').then(m => m.HelpAndSupportModule),
        data: {
            pageTitle: 'HELP & SUPPORT'
        },
        canActivate: [MobileGuard]
    },
    {
        path: 'account',
        loadChildren: () => import('@modules/account-mobile/account-mobile.module').then(m => m.AccountMobileModule),
        data: {
            pageTitle: 'ACCOUNT',
            hideHeader: true
        },
        canActivate: [MobileGuard]
    },
    {
        path: 'delete',
        loadChildren: () => import('@modules/delete-account/delete-account.module').then(m => m.DeleteAccountModule),
        data: {
            hideHeader: true
        },
        canActivate: [MobileGuard, AuthGuard]
    },
    {
        path: 'cart',
        loadChildren: () => import('@modules/cart/cart.module').then(m => m.CartModule),
        data: {
            pageTitle: 'CART'
        },
        resolve: {
            outletData: OutletResolver
        }
    },
    {
        path: 'credits',
        loadChildren: () => import('@modules/credit/credit.module').then(m => m.CreditModule),
        data: {
            pageTitle: 'CREDITS'
        },
        canActivate: [AuthGuard]
    },
    {
        path: 'offers',
        loadChildren: () => import('@modules/offers/offers.module').then(m => m.OffersModule),
        data: {
            pageTitle: 'OFFERS'
        }
    },
    {
        path: 'faq',
        loadChildren: () => import('@modules/faq/faq.module').then(m => m.FaqModule),
        data: {
            pageTitle: "FaQ's"
        }
    },
    {
        path: 'delivery_policy',
        loadChildren: () =>
            import('@project_app/modules/delivery-policies/delivery-policies.module').then(m => m.DeliveryPoliciesModule),
        data: {
            pageTitle: 'TERMS & CONDITIONS'
        }
    },

    {
        path: 'privacy_policy',
        loadComponent: () =>
            import('@shared/components/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
        data: {
            pageTitle: 'PRIVACY POLICY'
        }
    },
    {
        path: 'disclaimers',
        loadChildren: () => import('@project_app/modules/disclaimers/disclaimers.module').then(m => m.DisclaimersModule),
        data: {
            pageTitle: 'DISCLAIMERS'
        }
    },
    {
        path: 'about',
        loadChildren: () => import('@project_app/modules/about-us/about-us.module').then(m => m.AboutUsModule),
        data: {
            pageTitle: 'ABOUT US'
        }
    },
    {
        path: 'contact',
        loadChildren: () => import('@project_app/modules/contact-us/contact-us.module').then(m => m.ContactUsModule),
        data: {
            pageTitle: 'CONTACT US'
        }
    },
    {
        path: 'membership',
        loadChildren: () => import('@app/modules/branding-page/branding-page.module').then(m => m.BrandingPageModule),
        data: {
            pageTitle: 'EatClub',
            showCart: true
        },
        resolve: {
            outletData: OutletResolver
        }
    },
    {
        path: 'party_order',
        loadChildren: () => import('@modules/party-order/party-order.module').then(m => m.PartyOrderModule),
        data: {
            pageTitle: 'BULK ORDER'
        },
        canActivate: [AuthGuard]
    },
    {
        path: 'refer',
        loadChildren: () => import('@modules/refer/refer.module').then(m => m.ReferModule),
        data: {
            pageTitle: 'Refer & Earn',
            hideHeader: true
        },
        canActivate: [AuthGuard]
    },
    {
        path: 'refunds',
        loadChildren: () => import('@modules/refund/refund.module').then(m => m.RefundModule),
        data: {
            pageTitle: 'Refunds'
        },
        canActivate: [AuthGuard, MobileGuard]
    },
    {
        path: 'confirm_page',
        loadChildren: () => import('@modules/order-confirmation/order-confirmation.module').then(m => m.OrderConfirmationModule),
        data: {
            pageTitle: 'ORDER CONFIRMED',
            hideHeader: true
        }
    },
    {
        path: 'manage_orders',
        loadChildren: () => import('@modules/manage-orders/manage-orders.module').then(m => m.ManageOrdersModule),
        canActivate: [AuthGuard],
        data: {
            pageTitle: 'MANAGE ORDERS'
        }
    },
    {
        path: 'live_tracking',
        loadChildren: () => import('@modules/live-tracking/live-tracking.module').then(m => m.LiveTrackingModule),
        data: {
            pageTitle: 'LIVE TRACKING',
            hideHeader: true,
            preloadPath: ['confirm_page']
        }
    },
    // Third Party Routes
    {
        path: 't',
        loadChildren: () => import('@modules/third-party/third-party.module').then(m => m.ThirdPartyModule),
        data: {
            pageTitle: 'Feedback'
        }
    },
    // Add all the routes above this
    {
        path: '**',
        component: BrandsComponent,
        data: {
            pageTitle: 'BOX8',
            showAppDownloadOptions: true
        },
        resolve: {
            RouteResolver
        }
    }
];

export default routes;
